import React, { Suspense, lazy, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { ThemeProvider } from '../contexts/ThemeContext';
import { SiteDataProvider } from './data/SiteDataContext';
import { SeoProvider } from './data/SeoContext';
import SiteSeo from './components/SiteSeo';
import SiteAnalytics from './components/SiteAnalytics';
import SiteRedirects, { CatchAllRedirect } from './components/SiteRedirects';
import GrainCanvas from './components/GrainCanvas';
import SearchOverlay from './components/SearchOverlay';
import MobileNav from './components/MobileNav';
import SiteHeader from './components/SiteHeader';
import SiteSidebar from './components/SiteSidebar';
import SiteFooter from './components/SiteFooter';
import HomePage from './pages/HomePage';
import CategoryPage from './pages/CategoryPage';
import SeriesIndexPage from './pages/SeriesIndexPage';
import SeriesPage from './pages/SeriesPage';
import TagsPage from './pages/TagsPage';
import TagArchivePage from './pages/TagArchivePage';
import SearchPage from './pages/SearchPage';
import ArticlePage from './pages/ArticlePage';
import AboutPage from './pages/AboutPage';
import GradingPage from './pages/GradingPage';
import VideosPage from './pages/VideosPage';
import './site.css';

// Admin-only dashboards, code-split so they never weigh down public pages.
const SeoAdminPage = lazy(() => import('./pages/admin/SeoAdminPage'));
const AnalyticsAdminPage = lazy(() => import('./pages/admin/AnalyticsAdminPage'));
const InsightsAdminPage = lazy(() => import('./pages/admin/InsightsAdminPage'));
const GuideAdminPage = lazy(() => import('./pages/admin/GuideAdminPage'));
const MasterDashboardPage = lazy(() => import('./pages/admin/MasterDashboardPage'));

function Site() {
    const location = useLocation();
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    // Mark <html>/<body> while the new site is mounted so its global styles
    // (smooth scroll, black background) apply without touching the /old app.
    useEffect(() => {
        document.documentElement.classList.add('ds-html');
        document.body.classList.add('ds-body');
        return () => {
            document.documentElement.classList.remove('ds-html');
            document.body.classList.remove('ds-body');
        };
    }, []);

    // ESC closes the overlays, like the live theme.
    useEffect(() => {
        const onKeyDown = (event) => {
            if (event.key === 'Escape') {
                setIsSearchOpen(false);
                setIsMenuOpen(false);
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, []);

    // Scroll lock while the mobile nav is open.
    useEffect(() => {
        document.body.style.overflow = isMenuOpen ? 'hidden' : '';
        return () => {
            document.body.style.overflow = '';
        };
    }, [isMenuOpen]);

    // Fresh page starts at the top, like a full WordPress page load. Tag
    // selection changes (`sel`) deliberately keep the scroll position so
    // toggling filter chips doesn't yank the page — only path, page number,
    // and search query changes scroll.
    const params = new URLSearchParams(location.search);
    const scrollKey = `${location.pathname}|${params.get('p') || ''}|${params.get('q') || ''}`;
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [scrollKey]);

    useEffect(() => {
        setIsSearchOpen(false);
        setIsMenuOpen(false);
    }, [location.pathname, location.search]);

    return (
        <ThemeProvider>
            <SiteDataProvider>
                <SeoProvider>
                <SiteRedirects />
                <SiteSeo />
                <SiteAnalytics />
                <div className="ds-site">
                <GrainCanvas />
                <SearchOverlay open={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
                <MobileNav open={isMenuOpen} onClose={() => setIsMenuOpen(false)} />
                <SiteHeader
                    isSearchOpen={isSearchOpen}
                    isMenuOpen={isMenuOpen}
                    onToggleSearch={() => setIsSearchOpen((open) => !open)}
                    onOpenMenu={() => setIsMenuOpen(true)}
                />
                <div className="site-content-wrap">
                    <main className="main-content" id="main" role="main">
                        <Routes>
                            <Route path="/" element={<HomePage />} />
                            <Route path="page/:pageNum" element={<HomePage />} />
                            <Route path="category/:slug" element={<CategoryPage />} />
                            <Route path="category/:slug/page/:pageNum" element={<CategoryPage />} />
                            <Route path="tags" element={<TagsPage />} />
                            <Route path="tag/:tag" element={<TagArchivePage />} />
                            <Route path="tag/:tag/page/:pageNum" element={<TagArchivePage />} />
                            <Route path="search" element={<SearchPage />} />
                            <Route path="posts/:postId" element={<ArticlePage />} />
                            <Route path="about" element={<AboutPage />} />
                            <Route path="grading-system" element={<GradingPage />} />
                            <Route path="videos" element={<VideosPage />} />
                            <Route path="videos/page/:pageNum" element={<VideosPage />} />
                            <Route path="series" element={<SeriesIndexPage />} />
                            <Route path="series/:slug" element={<SeriesPage />} />
                            <Route
                                path="admin"
                                element={(
                                    <Suspense fallback={<p className="small-meta site-loading">Loading …</p>}>
                                        <MasterDashboardPage />
                                    </Suspense>
                                )}
                            />
                            <Route
                                path="admin/seo"
                                element={(
                                    <Suspense fallback={<p className="small-meta site-loading">Loading …</p>}>
                                        <SeoAdminPage />
                                    </Suspense>
                                )}
                            />
                            <Route
                                path="admin/analytics"
                                element={(
                                    <Suspense fallback={<p className="small-meta site-loading">Loading …</p>}>
                                        <AnalyticsAdminPage />
                                    </Suspense>
                                )}
                            />
                            <Route
                                path="admin/insights"
                                element={(
                                    <Suspense fallback={<p className="small-meta site-loading">Loading …</p>}>
                                        <InsightsAdminPage />
                                    </Suspense>
                                )}
                            />
                            <Route
                                path="admin/guide"
                                element={(
                                    <Suspense fallback={<p className="small-meta site-loading">Loading …</p>}>
                                        <GuideAdminPage />
                                    </Suspense>
                                )}
                            />
                            <Route path="*" element={<CatchAllRedirect />} />
                        </Routes>
                    </main>
                    <SiteSidebar />
                </div>
                    <SiteFooter />
                </div>
                </SeoProvider>
            </SiteDataProvider>
        </ThemeProvider>
    );
}

export default Site;
